import { useState } from 'react';

export default function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string || '').trim();

    if (honeypot) {
      setStatus('success');
      setEmail('');
      return;
    }

    if (!email.trim()) {
      setErrorMsg('Please enter your email address.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const payload = new URLSearchParams();
      payload.append('email', email.trim());

      const response = await fetch('https://readdy.ai/api/form/d9gbbm1gav9vgaom0opg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || 'Something went wrong. Please try again.';
        setErrorMsg(serverMsg);
        setStatus('error');
      }
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };

  return (
    <section id="newsletter" className="py-16 md:py-20 bg-background-100">
      <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
        <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
          Stay Updated
        </span>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
          Get the Latest Vigorbuy Spreadsheet Updates
        </h2>
        <p className="text-sm md:text-base text-foreground-600 mb-8">
          Subscribe to receive weekly product discoveries, new category additions, seasonal 
          highlights, and exclusive shopping tips delivered straight to your inbox.
        </p>

        <form onSubmit={handleSubmit} data-readdy-form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <div className="form-hp-container">
            <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
          </div>
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
            placeholder="Enter your email address"
            required
            className="flex-1 px-4 py-3 text-sm rounded-xl border border-background-200/70 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className="px-6 py-3 text-sm font-semibold rounded-xl bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
          </button>
        </form>

        {status === 'success' && (
          <p className="mt-4 text-sm text-accent-600 font-medium">
            Thank you for subscribing! Check your inbox for confirmation.
          </p>
        )}
        {status === 'error' && errorMsg && (
          <p className="mt-4 text-sm text-red-500">{errorMsg}</p>
        )}

        <p className="mt-4 text-xs text-foreground-400">
          No spam, ever. Unsubscribe anytime. We respect your privacy.
        </p>
      </div>
    </section>
  );
}